/**
 * PDP add-to-cart stepper — Flutter SurfaceCartStepper / ProductSurfaceCartControl
 * parity (2026-08-08). Full-width "Add to cart" pill; after the first add,
 * the same bar becomes an inline "+ qty (total) -" stepper — no drawer, no
 * navigation, center shows qty and running total (unit price x qty).
 *
 * Same in-flight-coalescing / direct-fetch approach as eros-plp-quick-add.js
 * (see that file for why: Flutter's equivalent write is local/Isar with no
 * network round-trip to guard against, so it has no debounce to port from —
 * this needs one because it's a real Shopify cart call).
 *
 * Event delegation, not per-node binding — Prestige's ProductRerender swaps
 * <buy-buttons> wholesale on every variant change (theme.js onRerender_fn,
 * `element.querySelector("buy-buttons").replaceWith(...)`), and neither
 * shopify:section:load nor eros:content-loaded fires for that swap. A
 * freshly-inserted stepper node was never getting (re)bound, so the
 * increment/decrement/add buttons went permanently dead after the first
 * variant switch — confirmed live 2026-08-09. eros-pdp-stock-notify.js
 * already documents and avoids this exact swap with a single delegated
 * document click listener; this file follows the same pattern instead of
 * per-node addEventListener.
 */
(function () {
  if (window.erosPdpCartStepperBound) return;
  window.erosPdpCartStepperBound = true;

  var DEFAULT_CAP = 6;

  // Per-root in-flight state, keyed by the current DOM node. A variant
  // switch replaces the root wholesale, so old entries just become
  // unreferenced and garbage-collect naturally — no state to migrate.
  var inFlightMap = new WeakMap();
  var pendingQtyMap = new WeakMap();

  function toast(message, kind) {
    if (window.ErosUI && typeof window.ErosUI.toast === 'function') {
      window.ErosUI.toast(message, kind);
    }
  }

  // DUPLICATE: keep in sync with eros-plp-quick-add.js's computeCap(). Not
  // extracted to a shared module — these two files load independently (PDP
  // vs PLP) with no guaranteed shared script between them, and consolidating
  // into eros-commerce-ui.js is deliberately avoided here to not collide
  // with the parallel money-formatter consolidation work landing there.
  function computeCap(root) {
    var tracked = root.getAttribute('data-inventory-tracked') === 'true';
    if (!tracked) return DEFAULT_CAP;
    if (root.getAttribute('data-inventory-policy') === 'continue') return DEFAULT_CAP;
    var qty = parseInt(root.getAttribute('data-inventory-quantity'), 10);
    if (isNaN(qty)) return DEFAULT_CAP;
    return Math.max(0, Math.min(qty, DEFAULT_CAP));
  }

  // window.Shopify.formatMoney is never actually defined anywhere in this
  // theme (confirmed live) — see eros-commerce-ui.js for the full history.
  // The fix now lives once there (window.ErosUI.formatMoneyCents), which
  // loads earlier in layout/theme.liquid <head> and is `defer`red the same
  // as this script, so it has always finished executing by the time this
  // IIFE runs. `cents` here is Shopify Cart-AJAX minor units
  // (unitCents * qty), matching formatMoneyCents's contract.
  function formatMoney(cents) {
    return window.ErosUI.formatMoneyCents(cents);
  }

  function findLine(cart, variantId) {
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === String(variantId)) return cart.items[i];
    }
    return null;
  }

  function setState(root, qty) {
    var addBtn = root.querySelector('[data-eros-pcs-add]');
    var bar = root.querySelector('[data-eros-pcs-bar]');
    var qtyEl = root.querySelector('[data-eros-pcs-qty]');
    var totalEl = root.querySelector('[data-eros-pcs-total]');
    var incBtn = root.querySelector('[data-eros-pcs-increment]');
    root.dataset.qty = String(qty);
    if (qty > 0) {
      addBtn.hidden = true;
      bar.hidden = false;
      qtyEl.textContent = String(qty);
      var unitCents = parseInt(root.getAttribute('data-unit-price-cents'), 10) || 0;
      totalEl.textContent = '(' + formatMoney(unitCents * qty) + ')';
      incBtn.disabled = qty >= computeCap(root);
    } else {
      addBtn.hidden = false;
      bar.hidden = true;
    }
  }

  function dispatchCartChange(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'eros:pdp-cart-stepper', cart: cart },
      })
    );
  }

  function cartRequest(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok) {
          var err = new Error((json && json.description) || 'cart request failed');
          err.responseJson = json;
          throw err;
        }
        return json;
      });
    });
  }

  function fetchCart() {
    return fetch('/cart.js').then(function (r) {
      return r.json();
    });
  }

  /**
   * Same fix as eros-plp-quick-add.js (see that file's comment for the
   * full root cause): theme.js's CartDrawer reads event.detail.cart.sections
   * unconditionally and throws if it's missing, silently aborting before
   * the drawer's item list updates. Collect the bundled section ids the
   * same way every other add/change flow in this theme does.
   */
  function collectSectionsToBundle() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function currentQty(root) {
    return parseInt(root.dataset.qty || '0', 10);
  }

  function revertToServerState(root, variantId) {
    fetchCart().then(function (cart) {
      var line = findLine(cart, variantId);
      setState(root, line ? line.quantity : 0);
    });
  }

  function afterSettle(root, variantId) {
    inFlightMap.set(root, false);
    var pendingQty = pendingQtyMap.get(root);
    if (pendingQty !== undefined && pendingQty !== null) {
      pendingQtyMap.set(root, null);
      runChange(root, variantId, pendingQty);
    }
  }

  function runChange(root, variantId, qty) {
    if (inFlightMap.get(root)) {
      pendingQtyMap.set(root, qty);
      setState(root, qty);
      return;
    }
    inFlightMap.set(root, true);
    setState(root, qty);
    var errorLabel = root.getAttribute('data-error-label') || '';
    var sections = collectSectionsToBundle();
    cartRequest('/cart/change.js', { id: variantId, quantity: qty, sections: sections.join(',') })
      .then(function (cart) {
        var line = findLine(cart, variantId);
        setState(root, line ? line.quantity : 0);
        dispatchCartChange(cart);
      })
      .catch(function () {
        revertToServerState(root, variantId);
        toast(errorLabel, 'error');
      })
      .finally(function () {
        afterSettle(root, variantId);
      });
  }

  function runAdd(root, variantId) {
    if (inFlightMap.get(root)) return;
    inFlightMap.set(root, true);
    setState(root, 1);
    var errorLabel = root.getAttribute('data-error-label') || '';
    var sections = collectSectionsToBundle();
    cartRequest('/cart/add.js', { id: variantId, quantity: 1, sections: sections.join(',') })
      .then(function (addResponse) {
        return fetchCart().then(function (cart) {
          cart.sections = addResponse.sections;
          return cart;
        });
      })
      .then(function (cart) {
        var line = findLine(cart, variantId);
        setState(root, line ? line.quantity : 1);
        dispatchCartChange(cart);
      })
      .catch(function () {
        setState(root, 0);
        toast(errorLabel, 'error');
      })
      .finally(function () {
        afterSettle(root, variantId);
      });
  }

  function onDocumentClick(event) {
    var root = event.target.closest('[data-eros-pdp-cart-stepper]');
    if (!root) return;
    var variantId = root.getAttribute('data-variant-id');

    if (event.target.closest('[data-eros-pcs-add]')) {
      runAdd(root, variantId);
      return;
    }
    if (event.target.closest('[data-eros-pcs-increment]')) {
      var next = Math.min(currentQty(root) + 1, computeCap(root));
      if (next === currentQty(root)) return;
      runChange(root, variantId, next);
      return;
    }
    if (event.target.closest('[data-eros-pcs-decrement]')) {
      runChange(root, variantId, Math.max(currentQty(root) - 1, 0));
    }
  }

  document.addEventListener('click', onDocumentClick);
})();
